import React, { useLayoutEffect, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PointerActivationConstraints } from '@dnd-kit/dom';
import { KeyboardSensor, PointerSensor } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import classNames from 'classnames';

import CardContainer from '../../../containers/CardContainer';
import styles from '../Board.module.scss';

const CardPointerSensor = PointerSensor.configure({
  activationConstraints: [
    new PointerActivationConstraints.Distance({
      value: 5,
    }),
  ],
});

function SortableCard({ id, listId, canEdit, hideParent, onSortableReady }) {
  const sortable = useSortable({
    id,
    index: 0, // initially a prop, but it was lagging when dropping a card that changed many positions
    // (= indexes). Now patched imperatively via refs (see List's useLayoutEffect).
    group: listId,
    type: 'Card',
    accept: ['Card'],
    disabled: !canEdit,
    sensors: [KeyboardSensor, CardPointerSensor],
  });

  const elementRef = useRef(null); // store the DOM element locally

  // register the Sortable instance with the parent List so it can update `sortable.index`
  // imperatively when card order changes without re-rendering this component
  const sortableInstance = sortable.sortable;

  // useLayoutEffect so this runs BEFORE the parent List's useLayoutEffect
  useLayoutEffect(() => {
    onSortableReady(id, sortableInstance);

    // re-submit the ref after the DnD Kit manager is initialized
    const el = elementRef.current;
    if (el) {
      Promise.resolve().then(() => {
        sortable.ref(el);
        sortable.handleRef(el);
      });
    }

    return () => onSortableReady(id, null);
  }, [id, sortable, sortableInstance, onSortableReady]);

  // one-shot re-registration to force DnD Kit to (re)attach the DOM attributes
  // (role / tabindex) when the element was not yet wired on first mount
  useEffect(() => {
    const el = elementRef.current;
    if (!el || el.getAttribute('role')) return; // card already wired

    setTimeout(() => {
      sortable.ref(null);
      sortable.handleRef(null);

      sortable.ref(el);
      sortable.handleRef(el);
    }, 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={(el) => {
        elementRef.current = el; // store the ref
        sortable.ref(el);
        sortable.handleRef(el);
      }}
      className={classNames(
        styles.card,
        canEdit && styles.draggable,
        sortable.isDragging && styles.dragging,
      )}
    >
      <CardContainer id={id} hideParent={hideParent} />
    </div>
  );
}

SortableCard.propTypes = {
  id: PropTypes.string.isRequired,
  listId: PropTypes.string.isRequired,
  canEdit: PropTypes.bool.isRequired,
  hideParent: PropTypes.bool,
  onSortableReady: PropTypes.func.isRequired,
};

SortableCard.defaultProps = {
  hideParent: false,
};

export default React.memo(SortableCard);
